/**
 * Shefira's clips (ADR 0067, data/assets/pixellab/dragon-shefira.json), as
 * the manifest names them and as they were drawn: every clip on one
 * 256×224 canvas with her landed feet on row 213, centred on column 128, so
 * a clip placed by its feet lines up pixel for pixel with the others. Flight
 * is the whole canvas moved in code; the clips hold only the motion around
 * the pose. The shadow is each hover frame's silhouette squashed to a third
 * of its height (256×75), its ground row 71 under her feet.
 */

export const DRAGON_CLIPS = {
  idle: { art: 'dragon.shefira-idle', frames: 8, ms: 110, loop: true, feet: { x: 128, y: 213 } },
  hover: { art: 'dragon.shefira-hover', frames: 8, ms: 90, loop: true, feet: { x: 128, y: 213 } },
  landing: {
    art: 'dragon.shefira-landing',
    frames: 13,
    ms: 100,
    loop: false,
    feet: { x: 128, y: 213 },
  },
  roar: { art: 'dragon.shefira-roar', frames: 25, ms: 90, loop: false, feet: { x: 128, y: 213 } },
  takeoff: {
    art: 'dragon.shefira-takeoff',
    frames: 13,
    ms: 90,
    loop: false,
    feet: { x: 128, y: 213 },
  },
  shadow: { art: 'dragon.shefira-shadow', frames: 8, ms: 90, loop: true, feet: { x: 128, y: 71 } },
} as const;

/**
 * Her fight in the Aerie (ADR 0068), drawn on the same canvas and feet as the
 * clips above; kept apart so a cinematic never waits on sheets it does not
 * play. Every one starts and ends on the reference pose unless noted, facing
 * the camera (one direction only: the fight turns the ground shapes, not her).
 * - breath: 0-8 rears then breathes, 8-15 the held breath (15 loops back to
 *   8), 16-23 the fire dies out; the fire reaches the canvas foot.
 * - tail: the flaming sweep crosses in front of her at 2-7.
 * - wings: the beat lands at 6, a ring of fire at 6-8.
 * - dive: 0 = diving at the camera (hold it while the canvas falls in
 *   code), impact at 5, standing at 12.
 * - hurt: Ser Givrecoeur's sword strikes at 1 and stays in her chest.
 * - takeoff-wounded: from hurt's last frame to a wounded flight pose, sword kept.
 * - fold: 0 = the reference, 4 = wings folded for walking (played backwards to spread them).
 * - walk: a loop in place with the wings folded, 0 = fold's last frame; the
 *   game moves her canvas across the ground while it plays.
 */
export const SHEFIRA_COMBAT_CLIPS = {
  breath: {
    art: 'dragon.shefira-breath',
    frames: 24,
    ms: 90,
    loop: false,
    feet: { x: 128, y: 213 },
  },
  tail: { art: 'dragon.shefira-tail', frames: 13, ms: 80, loop: false, feet: { x: 128, y: 213 } },
  wings: { art: 'dragon.shefira-wings', frames: 13, ms: 80, loop: false, feet: { x: 128, y: 213 } },
  dive: { art: 'dragon.shefira-dive', frames: 13, ms: 80, loop: false, feet: { x: 128, y: 213 } },
  hurt: { art: 'dragon.shefira-hurt', frames: 9, ms: 90, loop: false, feet: { x: 128, y: 213 } },
  'takeoff-wounded': {
    art: 'dragon.shefira-takeoff-wounded',
    frames: 13,
    ms: 90,
    loop: false,
    feet: { x: 128, y: 213 },
  },
  fold: { art: 'dragon.shefira-fold', frames: 5, ms: 80, loop: false, feet: { x: 128, y: 213 } },
  walk: { art: 'dragon.shefira-walk', frames: 16, ms: 70, loop: true, feet: { x: 128, y: 213 } },
} as const;

export type ClipName = keyof typeof DRAGON_CLIPS;
export type DragonArt = (typeof DRAGON_CLIPS)[ClipName]['art'];

/** Every sheet her scenes may ask for. */
export const DRAGON_ART: readonly DragonArt[] = Object.values(DRAGON_CLIPS).map((c) => c.art);

/** The frame a clip shows `elapsed` ms after it started: a loop wraps, a one-shot holds its last. */
export function frameOf(clip: ClipName, elapsed: number): number {
  const { frames, ms, loop } = DRAGON_CLIPS[clip];
  const index = Math.floor(Math.max(0, elapsed) / ms);
  return loop ? index % frames : Math.min(frames - 1, index);
}
